import { GetWorkflowSettingsResult } from "generated/graphql";
import React from "react";
import { useCrmMode } from "store/selectors/hooks";
import { workflowAnchoredOffsetRecipe } from "../manage-workflows-helper";
import { EditableWorkflow } from "../types";
import { SingleActionSummary } from "../workflow-action-summary";
import {
    describeActionInterval,
    useWorkflowDelayDescription,
} from "../workflow-delay-summary";
import {
    WorkflowClientStatusesSummary,
    WorkflowTriggerSummary,
} from "../workflow-trigger-summary";
import {
    actionTimelineLabel,
    workflowActionChips,
    workflowActionTimings,
    workflowEntityTypeLabel,
} from "./builder-helper";
import { ActionChip } from "./sequence-strip";

interface RowProps {
    label: string;
    children: React.ReactNode;
}

const actionTimingText = (timing: string | null, timeline: string | null): string =>
    [timing, timeline].filter(part => !!part).join(" · ");

const Row: React.FC<RowProps> = props => {
    const { label, children } = props;
    return (
        <div className="flex gap-3 text-sm">
            <span className="w-28 shrink-0 text-gray-400">{label}</span>
            <div className="text-gray-700 min-w-0 flex-1">{children}</div>
        </div>
    );
};

interface WorkflowSummaryCardProps {
    workflow: EditableWorkflow;
    workflowSettings: GetWorkflowSettingsResult;
}

export const WorkflowSummaryCard: React.FC<WorkflowSummaryCardProps> = props => {
    const { workflow, workflowSettings } = props;
    const delayDescription = useWorkflowDelayDescription(workflow, workflowSettings);
    const timings = workflowActionTimings(
        workflow,
        workflowAnchoredOffsetRecipe(workflow, workflowSettings),
    );
    const chips = workflowActionChips(workflow);
    const { mode } = useCrmMode();

    return (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
            <Row label="Workflow">
                <span className="font-medium">{workflow.name}</span>
            </Row>
            <Row label="Record type">
                {workflowEntityTypeLabel(workflow.attachTo, mode)}
            </Row>
            <Row label="Trigger">
                <div className="space-y-2">
                    <WorkflowTriggerSummary workflow={workflow} />
                    <WorkflowClientStatusesSummary workflow={workflow} />
                </div>
            </Row>
            <Row label="Actions">
                <ol className="space-y-3">
                    {workflow.actions.map((action, position) => (
                        <li key={action.id ?? position} className="space-y-1">
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="font-semibold text-gray-800">
                                    Action {position + 1}
                                </span>
                                {chips[position] ? (
                                    <ActionChip chip={chips[position]} />
                                ) : null}
                            </div>
                            <div className="text-xs text-gray-500">
                                {actionTimingText(
                                    position === 0
                                        ? delayDescription
                                        : describeActionInterval(action.delayRecipe),
                                    actionTimelineLabel(timings, position),
                                )}
                            </div>
                            <SingleActionSummary
                                workflow={workflow}
                                action={action}
                                workflowSettings={workflowSettings}
                            />
                        </li>
                    ))}
                </ol>
            </Row>
        </div>
    );
};
